import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { type ChatMessage } from "~hooks/useChat";
import { useSelector } from "~hooks/useTypedRedux";
import { ChatMessage as ChatMessageComponent } from "~ui/atoms/Chat/ChatMessage";

import { type State } from "../../../state/state";

interface ChatMessageListProps {
  messages: ChatMessage[];
  isConnected: boolean;
  /** When set (e.g. law enforcement chat), use this id for "you" styling instead of the signed-in app user. */
  highlightUserId?: string;
}

interface GroupedMessage extends Omit<ChatMessage, "timestamp"> {
  timestamp: string;
  bodies: string[];
}

const groupMessagesByUserAndMinute = (messages: ChatMessage[]): GroupedMessage[] => {
  const grouped: GroupedMessage[] = [];
  const reversedMessages = [...messages].reverse();

  for (const currentMessage of reversedMessages) {
    const prevMessage = grouped[grouped.length - 1];
    // Handle both string and Date timestamp types
    const currentTime = DateTime.fromISO(
      typeof currentMessage.timestamp === "string" ? currentMessage.timestamp : currentMessage.timestamp.toISOString(),
    );

    if (
      prevMessage &&
      prevMessage.userId === currentMessage.userId &&
      DateTime.fromISO(prevMessage.timestamp).hasSame(currentTime, "minute")
    ) {
      // Add this message's body to the previous group
      prevMessage.bodies.push(currentMessage.body);
    } else {
      // Start a new group
      grouped.push({
        ...currentMessage,
        bodies: [currentMessage.body],
        timestamp:
          typeof currentMessage.timestamp === "string"
            ? currentMessage.timestamp
            : currentMessage.timestamp.toISOString(),
      });
    }
  }

  return grouped;
};

export const ChatMessageList = ({ messages, isConnected, highlightUserId }: ChatMessageListProps) => {
  const reduxUserId = useSelector((state: State) => state.user?.id);
  const loggedInUserId = highlightUserId ?? reduxUserId;

  if (!isConnected) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Typography color="text.secondary">Connecting to chat...</Typography>
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <Typography color="text.secondary">No messages yet</Typography>
      </Box>
    );
  }

  const groupedMessages = groupMessagesByUserAndMinute(messages);

  const messagesByDate = groupedMessages.reduce<{ date: string; messages: GroupedMessage[] }[]>((acc, message) => {
    const dt = DateTime.fromISO(message.timestamp);
    const dateStr = dt.toFormat("LLL d, yyyy"); // e.g. "Oct 14, 2023"

    const lastGroup = acc[acc.length - 1];
    if (lastGroup && lastGroup.date === dateStr) {
      lastGroup.messages.push(message);
    } else {
      acc.push({ date: dateStr, messages: [message] });
    }
    return acc;
  }, []);

  return (
    <Box>
      {messagesByDate.map(({ date, messages }) => (
        <Box key={date}>
          <Box
            sx={{
              "&::before, &::after": {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                content: '""',
                flex: 1,
              },
              alignItems: "center",
              display: "flex",
              my: 2,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: "medium",
                px: 2,
              }}
            >
              {date}
            </Typography>
          </Box>
          {messages.map((message) => (
            <ChatMessageComponent
              key={message.id}
              {...message}
              body={message.bodies.join("\n")}
              loggedInUserId={loggedInUserId}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};
